import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { api } from "@/lib/api";
import { useProjectCtx } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { getErrorMessage } from "@/lib/errors";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const inrShort = (v) => {
  const n = Number(v || 0);
  const a = Math.abs(n);
  if (a >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (a >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return inr(n);
};
const pct = (v) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(1)}%`);
const CATEGORY_LABEL = { SUBCONTRACT: "Subcontract", MATERIAL: "Material", PLANT: "Equipment", LABOUR: "Labour", OTHER: "Other" };

export default function ProjectFinance() {
  const { projectId } = useProjectCtx();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get(`/projects/${projectId}/finance`);
      setData(res);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <p className="text-sm text-muted-foreground" data-testid="project-finance-loading">Loading project finance…</p>;
  if (!data) return null;

  const { project, revenue, cost, margin, cash, payables, monthly, budget_lines, warnings } = data;
  const marginTone = (v) => (Number(v) < 0 ? "rose" : "emerald");

  return (
    <div className="space-y-6" data-testid="project-finance-page">
      <PageHeader
        title="Project Finance"
        description={`${project.code} · ${project.name} — revenue, cost, margin, cash and payables in one place. Posted documents only.`}
        action={<Button variant="outline" onClick={load} disabled={loading}><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh</Button>}
      />

      {warnings.length > 0 && (
        <div className="space-y-1.5 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-400" data-testid="project-finance-warnings">
          {warnings.map((w, i) => <p key={i} className="flex gap-2"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{w}</p>)}
        </div>
      )}

      {/* Headline */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Tile label="Contract value" value={inrShort(project.contract_value)} sub={`${pct(revenue.progress_pct)} work done`} />
        <Tile label="Billed to client" value={inrShort(revenue.billed_gross)} sub={`${pct(revenue.certified_pct)} certified · ${revenue.bills_count} RA bill(s)`} />
        <Tile label="Actual cost" value={inrShort(cost.actual)} sub={`+ ${inrShort(cost.committed)} committed`} />
        <Tile label="Gross margin" value={inrShort(margin.gross_margin)} sub={`${pct(margin.gross_margin_pct)} of billed`} tone={marginTone(margin.gross_margin)} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Revenue (client)">
          <Row label="Contract value" value={inr(project.contract_value)} />
          <Row label={`Work done (earned) · ${pct(revenue.progress_pct)}`} value={inr(revenue.earned_value)} />
          <Row label="Billed (gross, before GST)" value={inr(revenue.billed_gross)} strong />
          <Row label="Work done but not yet billed" value={inr(revenue.unbilled_work)} tone={Number(revenue.unbilled_work) > 0 ? "amber" : undefined} />
          <Row label="GST billed" value={inr(revenue.billed_gst)} muted />
          <Row label="Received from client" value={inr(revenue.received)} />
          <Row label="Receivable outstanding" value={inr(revenue.receivable_outstanding)} strong tone={Number(revenue.receivable_outstanding) > 0 ? "amber" : undefined} />
          <Row label="Retention held by client" value={inr(revenue.retention_held_by_client)} muted />
          <Row label="TDS deducted by client" value={inr(revenue.tds_deducted_by_client)} muted />
          {revenue.draft_bills > 0 && <p className="pt-2 text-xs text-muted-foreground">{revenue.draft_bills} draft RA bill(s) not yet posted — not included above.</p>}
        </Card>

        <Card title="Cost">
          {Object.keys(cost.by_category).length === 0 && <p className="py-1 text-sm text-muted-foreground">No cost booked yet.</p>}
          {Object.entries(cost.by_category).map(([k, v]) => <Row key={k} label={CATEGORY_LABEL[k] || k} value={inr(v)} />)}
          <Row label="Actual cost" value={inr(cost.actual)} strong />
          <Row label="Committed · subcontract (open WOs)" value={inr(cost.committed_subcontract)} muted />
          <Row label="Committed · material (open POs)" value={inr(cost.committed_material)} muted />
          <Row label="Actual + committed" value={inr(Number(cost.actual) + Number(cost.committed))} />
          <Row label="Approved budget" value={Number(cost.budget) ? inr(cost.budget) : "No approved budget"} />
          {Number(cost.budget) > 0 && <Row label="Budget used (actual + committed)" value={pct(cost.budget_used_pct)} tone={cost.budget_used_pct > 100 ? "rose" : undefined} />}
        </Card>

        <Card title="Margin & forecast">
          <Row label="Gross margin (billed − actual cost)" value={inr(margin.gross_margin)} strong tone={marginTone(margin.gross_margin)} />
          <Row label="Gross margin %" value={pct(margin.gross_margin_pct)} tone={marginTone(margin.gross_margin)} />
          <Row label="Forecast final cost" value={inr(cost.forecast_final_cost)} />
          <Row label="Forecast margin at completion" value={inr(margin.forecast_margin)} strong tone={marginTone(margin.forecast_margin)} />
          <Row label="Forecast margin %" value={pct(margin.forecast_margin_pct)} tone={marginTone(margin.forecast_margin)} />
          <p className="pt-2 text-xs text-muted-foreground">Forecast based on {cost.forecast_basis}.</p>
        </Card>

        <Card title="Cash & payables">
          <Row label="Cash in (client payments)" value={inr(cash.cash_in)} />
          <Row label="Cash out (paid to vendors & subcontractors)" value={inr(cash.cash_out)} />
          <Row label="Net cash position" value={inr(cash.net_cash)} strong tone={marginTone(cash.net_cash)} />
          <div className="my-2 border-t border-border" />
          <Row label="Owed to subcontractors" value={inr(payables.subcontractors_outstanding)} />
          <Row label="Owed to suppliers" value={inr(payables.suppliers_outstanding)} />
          <Row label="Total payables" value={inr(payables.total_outstanding)} strong tone={Number(payables.total_outstanding) > 0 ? "amber" : undefined} />
          <Row label="Retention held from subcontractors" value={inr(payables.retention_held_from_subcontractors)} muted />
          <Row label="TDS deducted from subcontractors" value={inr(payables.tds_deducted_from_subcontractors)} muted />
        </Card>
      </div>

      {monthly.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-4" data-testid="project-finance-monthly">
          <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Month by month (last 12 months)</p>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthly.map((m) => ({ ...m, billed: Number(m.billed), cost: Number(m.cost), cash_in: Number(m.cash_in), cash_out: Number(m.cash_out) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" />
                <XAxis dataKey="month" stroke="#64748b" fontSize={11} />
                <YAxis stroke="#64748b" fontSize={11} tickFormatter={inrShort} width={80} />
                <Tooltip formatter={(v) => inr(v)} />
                <Legend />
                <Bar dataKey="billed" name="Billed" fill="#E3A817" radius={[3, 3, 0, 0]} />
                <Bar dataKey="cost" name="Cost" fill="#EF4444" radius={[3, 3, 0, 0]} />
                <Bar dataKey="cash_in" name="Cash in" fill="#10B981" radius={[3, 3, 0, 0]} />
                <Bar dataKey="cash_out" name="Cash out" fill="#64748B" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {budget_lines.length > 0 && (
        <div className="rounded-xl border border-border bg-card" data-testid="project-finance-budget-lines">
          <p className="border-b border-border p-4 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Budget lines needing attention (lowest remaining first)</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground">
                <tr>{["WBS", "Budget", "Actual", "Committed", "Remaining"].map((h, i) => <th key={h} className={`px-4 py-2 font-medium ${i ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {budget_lines.map((r) => (
                  <tr key={r.wbs_item_id} className="border-t border-border">
                    <td className="px-4 py-2 text-foreground"><span className="font-mono text-xs text-muted-foreground">{r.code}</span> {r.name}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs">{inr(r.budget)}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs">{inr(r.actual)}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs">{inr(r.committed)}</td>
                    <td className={`px-4 py-2 text-right font-mono text-xs font-semibold ${r.over_budget ? "text-rose-500" : "text-emerald-600 dark:text-emerald-400"}`}>{inr(r.variance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const TONE = { rose: "text-rose-500", emerald: "text-emerald-600 dark:text-emerald-400", amber: "text-amber-600 dark:text-amber-400" };

function Tile({ label, value, sub, tone }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid={`project-finance-tile-${label.toLowerCase().replaceAll(" ", "-")}`}>
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mt-2 font-mono text-lg font-bold ${TONE[tone] || "text-foreground"}`}>{value}</p>
      {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">{title}</p>
      <div className="divide-y divide-border/60">{children}</div>
    </div>
  );
}

function Row({ label, value, strong, muted, tone }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5 text-sm">
      <span className={muted ? "text-muted-foreground" : "text-foreground"}>{label}</span>
      <span className={`font-mono ${strong ? "font-semibold" : ""} ${TONE[tone] || (muted ? "text-muted-foreground" : "text-foreground")}`}>{value}</span>
    </div>
  );
}
